if (!customElements.get('theme-email-popup')) {
  customElements.define('theme-email-popup', class ThemeEmailPopup extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('.tep__overlay');
      this.dialog = this.querySelector('.tep__dialog');
      if (!this.overlay || !this.dialog || this.dataset.bound === 'true') return;

      this.dataset.bound = 'true';
      this.storageKey = `theme-email-popup:${this.dataset.sectionId}`;
      this.previouslyFocused = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.onExitIntent = this.onExitIntent.bind(this);

      this.querySelectorAll('[data-tep-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(true));
      });

      this.querySelectorAll('[data-tep-copy]').forEach((button) => {
        button.addEventListener('click', () => this.copyCode(button));
      });

      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay && this.dataset.closeOverlay === 'true') this.close(true);
      });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
        });
      }

      if (this.dataset.success === 'true') {
        requestAnimationFrame(() => this.open());
        return;
      }

      if (this.dataset.enabled !== 'true' || !this.shouldDisplay()) return;
      this.schedule();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('mouseout', this.onExitIntent);
    }

    schedule() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const delay = Number(mobile ? this.dataset.mobileDelay : this.dataset.delay) || 0;

      if (!mobile && this.dataset.trigger === 'exit') {
        document.addEventListener('mouseout', this.onExitIntent);
        return;
      }

      this.timer = window.setTimeout(() => this.open(), delay);
    }

    onExitIntent(event) {
      if (event.clientY > 8 || event.relatedTarget) return;
      document.removeEventListener('mouseout', this.onExitIntent);
      this.open();
    }

    shouldDisplay() {
      if (window.Shopify && Shopify.designMode) return false;
      const frequency = this.dataset.frequency;

      try {
        if (frequency === 'session') return sessionStorage.getItem(this.storageKey) !== 'dismissed';
        if (frequency === 'days') {
          const dismissedAt = Number(localStorage.getItem(this.storageKey));
          const days = Number(this.dataset.days) || 7;
          return !dismissedAt || Date.now() - dismissedAt >= days * 86400000;
        }
      } catch (error) {
        return true;
      }

      return true;
    }

    rememberDismissal() {
      try {
        if (this.dataset.frequency === 'session') sessionStorage.setItem(this.storageKey, 'dismissed');
        if (this.dataset.frequency === 'days') localStorage.setItem(this.storageKey, String(Date.now()));
      } catch (error) {
        // Storage can be blocked by privacy settings; closing must still work.
      }
    }

    async copyCode(button) {
      const code = button.querySelector('[data-tep-code]')?.textContent?.trim();
      const status = this.querySelector('[data-tep-copy-status]');
      if (!code) return;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(code);
        } else {
          const temporary = document.createElement('textarea');
          temporary.value = code;
          temporary.setAttribute('readonly', '');
          temporary.style.position = 'fixed';
          temporary.style.opacity = '0';
          document.body.appendChild(temporary);
          temporary.select();
          document.execCommand('copy');
          temporary.remove();
        }
        if (status) status.textContent = button.dataset.copiedLabel || 'Copied';
      } catch (error) {
        if (status) status.textContent = code;
      }
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.previouslyFocused = document.activeElement;
      this.overlay.hidden = false;
      document.documentElement.classList.add('tep-lock');
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        window.setTimeout(() => {
          const firstInput = this.querySelector('input, [data-tep-close], a[href], button');
          if (firstInput) firstInput.focus({ preventScroll: true });
        }, 230);
      });
    }

    close(remember) {
      if (!this.classList.contains('is-open')) return;
      if (remember) this.rememberDismissal();
      this.classList.remove('is-open');
      document.documentElement.classList.remove('tep-lock');
      document.removeEventListener('keydown', this.onKeydown);
      window.setTimeout(() => {
        this.overlay.hidden = true;
        if (this.previouslyFocused && this.previouslyFocused.focus) this.previouslyFocused.focus({ preventScroll: true });
      }, 220);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
}
